import React from 'react';

import { Badge, Button } from '@ergeon/core-components';
import { ArrowTopRightOnSquareIcon } from '@ergeon/icons/hero/micro';

import { UnifiedDialogEntry } from '../../../../../types';

type StatusType =
  | 'quote_status'
  | 'customer_order'
  | 'order_project_status'
  | 'sales_rep_assignment'
  | 'primary_approval'
  | 'neighbor_approval'
  | 'own_approval'
  | 'permits_permit_request_status';

interface StatusChangeContextMessageProps {
  adminLink?: string | null;
  contextData?: UnifiedDialogEntry['context_data'];
}

const getStatusBadgeFlavor = (
  statusType: StatusType | string,
  statusCode: string | null,
): 'success' | 'warning' | 'danger' | 'primary' | undefined => {
  switch (statusType) {
    case 'quote_status':
      switch (statusCode) {
        case 'app':
          return 'success';
        case 'snt':
          return 'primary';
        case 'cancelled':
          return 'danger';
        default:
          return undefined;
      }

    case 'customer_order':
      switch (statusCode) {
        case 'won':
          return 'success';
        case 'scheduled':
        case 'quote_reviewed':
          return 'primary';
        case 'on_hold':
          return 'warning';
        case 'lost':
          return 'danger';
        default:
          return undefined;
      }

    case 'order_project_status':
      switch (statusCode) {
        case 'project_completed':
          return 'success';
        case 'project_scheduled':
        case 'pre_visit_done':
          return 'primary';
        case 'pre_visit_agreed':
        case 'remote_previsit_agreed':
          return 'primary';
        case 'on_hold':
          return 'warning';
        default:
          return undefined;
      }

    case 'sales_rep_assignment':
      return 'primary';

    case 'primary_approval':
    case 'neighbor_approval':
    case 'own_approval':
      return 'success';

    case 'permits_permit_request_status':
      switch (statusCode) {
        case 'permit_issued':
          return 'success';
        case 'permit_submitted':
          return 'primary';
        case 'permit_cancelled':
          return 'danger';
        default:
          return undefined;
      }

    default:
      return undefined;
  }
};

const formatObjectType = (objectType: string | null | undefined): string => {
  if (!objectType) return 'Item';
  return objectType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const StatusChangeContextMessage: React.FC<StatusChangeContextMessageProps> = ({
  adminLink,
  contextData,
}) => {
  const statusType = contextData?.status_type;
  const statusCode = contextData?.status_code;
  const badgeFlavor = statusType ? getStatusBadgeFlavor(statusType, statusCode ?? null) : undefined;

  const isSalesRepAssignment = statusType === 'sales_rep_assignment';
  const isApproval = ['primary_approval', 'neighbor_approval', 'own_approval'].includes(statusType || '');

  const currentValue = isSalesRepAssignment ?
    contextData?.sales_rep_name :
    contextData?.status_label;
  const previousValue = isSalesRepAssignment ?
    contextData?.prev_sales_rep_name :
    contextData?.prev_status_label;
  const hasPrevious = Boolean(previousValue);

  const badgeValue = isApproval ? 'Approved' : currentValue || 'Status';

  return (
    <div className="flex flex-col gap-1 w-full text-center">
      <div className="flex flex-wrap justify-center items-center gap-2 paragraph-2">
        {!isSalesRepAssignment && <span>Status update:</span>}
        <Button
          asAnchor
          flavor="regular"
          href={adminLink || '#'}
          size="small"
          target="_blank"
          taste="solid">
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
          {formatObjectType(contextData?.object_type)} #{contextData?.object_id}
        </Button>
        {isSalesRepAssignment && !hasPrevious && <span>assigned to</span>}
        {isSalesRepAssignment && hasPrevious && <span>reassigned</span>}
        {!hasPrevious && <Badge flavor={badgeFlavor} value={badgeValue} />}
      </div>

      {contextData?.amount && <div className="paragraph-2 font-semibold">{contextData.amount}</div>}

      {hasPrevious && (
        <div className="flex justify-center items-center gap-2 paragraph-3">
          <span className="text-gray-500">From {previousValue} to</span>
          <Badge flavor={badgeFlavor} value={badgeValue} />
        </div>
      )}

      {isApproval && contextData?.neighbor_name && (
        <div className="paragraph-3 text-gray-500">by {contextData.neighbor_name}</div>
      )}

      {isSalesRepAssignment && contextData?.assignment_type && (
        <div className="paragraph-3 text-gray-500">
          <span className="font-semibold">Assignment type:</span> {contextData.assignment_type}
        </div>
      )}

      {(contextData?.reason_text || contextData?.reason) && (
        <div className="paragraph-3 text-gray-500">
          <span className="font-semibold">Reason:</span> <span className="italic">{contextData?.reason_text || contextData?.reason}</span>
        </div>
      )}

      {contextData?.object_type !== 'order' && contextData?.order_id && (
        <div className="paragraph-3 text-gray-500">Order: #{contextData.order_id}</div>
      )}

      {(contextData?.quoting_tool_link || contextData?.draft_editor_link) && (
        <div className="flex justify-center items-center gap-2">
          {contextData?.quoting_tool_link && (
            <Button
              asAnchor
              flavor="regular"
              href={contextData.quoting_tool_link}
              size="small"
              target="_blank"
              taste="boundless">
              <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
              Quoting tool
            </Button>
          )}
          {contextData?.draft_editor_link && (
            <Button
              asAnchor
              flavor="regular"
              href={contextData.draft_editor_link}
              size="small"
              target="_blank"
              taste="boundless">
              <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
              DET
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
